import React, { useState, ChangeEvent } from 'react';
import { Shield, User, Lock, ArrowRight } from 'lucide-react';
import InputField from './InputField';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface LoginFormProps {
  onLogin: () => void;
}

interface LoginResponse {
  code: number;
  error?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formType, setFormType] = useState<'login' | 'register' | 'forgotPassword'>('login');
  const navigate = useNavigate();
  const { fetchUserInfo } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    if (formType === 'login') {
      try {
        const response = await fetch('/tryLogin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ username, password })
        });

        const data = await response.json();

        if (data.code === 200) {
          await fetchUserInfo();
          onLogin();
          navigate('/user-panel');
        } else {
          setError(data.error || 'Invalid username or password');
        }
      } catch (err) {
        setError('Failed to connect to the server');
      }
    } else if (formType === 'register') {
      try {
        const response = await fetch('/tryRegister', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username,
            email,
            password,
            repeatPassword: confirmPassword
          })
        });

        const data: LoginResponse = await response.json();

        console.log(data)
        if (data.code === 200) {
          setSuccess('Registration success, Please log in.');
          setFormType('login');
          setUsername('');
          setPassword('');
          setEmail('');
          setConfirmPassword('');
        } else if (data.code === 401) {
          setError(data.error || 'Registration failed');
        } else {
          setError('An unexpected error occurred');
        }
      } catch (err) {
        setError('Failed to connect to the server');
        console.log(err);
      }
    }

    setIsLoading(false);
  };

  const handleInputChange = (setter: (value: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
  };

  // Form title and description based on form type
  const getFormMetadata = () => {
    switch (formType) {
      case 'register':
        return {
          description: "Sign up to access the user dashboard"
        };
      case 'forgotPassword':
        return {
          title: "Reset Password",
          description: "We'll send a reset link to your email"
        };
      default:
        return {
          description: "Sign in to access the user dashboard"
        };
    }
  };

  const { description } = getFormMetadata();

  return (
    <div className="max-w-md w-full mx-4 relative">
      {/* Glow effects */}
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-blue-400/20 rounded-2xl blur-lg"></div>
      <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/10 to-blue-400/10 rounded-2xl blur-xl"></div>
      
      {/* Card content */}
      <div className="bg-gray-900/70 backdrop-blur-sm border border-gray-800 rounded-xl p-6 relative">
        <div className="flex justify-center mb-4">
          <img 
            src="/images/logo.png" 
            alt="Recoil.club Logo" 
            className="w-48 h-48"
          />
        </div>
        
        <p className="text-gray-400 text-center mb-4 text-sm">
          {description}
        </p>
        
        <form onSubmit={handleSubmit}>
          {formType === 'login' && (
            <>
              <InputField
                icon={User} 
                type="text"
                placeholder="Username"
                value={username}
                onChange={handleInputChange(setUsername)}
              />
              <InputField
                icon={Lock} 
                type="password"
                placeholder="Password"
                value={password}
                onChange={handleInputChange(setPassword)}
              />
              {error && (
                <div className="text-red-400 text-sm mt-2 text-center">
                  {error}
                </div>
              )}
              {success && (
                <div className="text-green-400 text-sm mt-2 text-center">
                  {success}
                </div>
              )}
            </>
          )}
          
          {formType === 'register' && (
            <>
              <InputField
                icon={User} 
                type="text"
                placeholder="Username"
                value={username}
                onChange={handleInputChange(setUsername)}
              />
              <InputField
                icon={User} 
                type="email"
                placeholder="Email"
                value={email}
                onChange={handleInputChange(setEmail)}
              />
              <InputField
                icon={Lock} 
                type="password"
                placeholder="Password"
                value={password}
                onChange={handleInputChange(setPassword)}
              />
              <InputField
                icon={Lock} 
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={handleInputChange(setConfirmPassword)}
              />
              {error && (
                <div className="text-red-400 text-sm mt-2 text-center">
                  Registration failed, {error}
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-400 to-blue-500 text-white py-2.5 px-5 rounded-lg font-medium hover:from-blue-500 hover:to-blue-600 transition-all duration-200 flex items-center justify-center mt-4"
            disabled={isLoading}
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <>
                {formType === 'login' ? 'Sign In' : 'Register'} <ArrowRight className="ml-1 w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Show register link only on login form */}
        {formType === 'login' && (
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              Don't have an account? <button 
                className="text-blue-400 hover:text-blue-300 bg-transparent border-none p-0 cursor-pointer"
                onClick={() => setFormType('register')}
              >
                Register now
              </button>
            </p>
          </div>
        )}

        {/* Show back to login link on register form */}
        {formType === 'register' && (
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              Already have an account? <button 
                className="text-blue-400 hover:text-blue-300 bg-transparent border-none p-0 cursor-pointer"
                onClick={() => setFormType('login')}
              >
                Back to Login
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm; 